/**
 * Checkpoint utility functions
 */

// Frequency type
export type Frequency = 'Daily' | 'Weekly' | 'Fortnightly' | 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly';

// Validation limits
export const VALIDATION_LIMITS = {
  NAME_MIN: 2,
  NAME_MAX: 200,
  DESCRIPTION_MAX: 2000,
  REQUIREMENT_MAX: 1000,
};

/**
 * Get maximum allowed grace period based on frequency
 * Grace period should not exceed the frequency duration
 * @param frequency - The checkpoint frequency
 * @returns Maximum grace period in days
 */
export const getMaxBufferTime = (frequency: Frequency): number => {
  const maxBufferDays: Record<Frequency, number> = {
    'Daily': 1,
    'Weekly': 7,
    'Fortnightly': 14,
    'Monthly': 30,
    'Quarterly': 90,
    'Half-Yearly': 180,
    'Yearly': 365
  };

  return maxBufferDays[frequency] || 1;
};

/**
 * Get default grace period in days based on checkpoint frequency
 * @param frequency - The checkpoint frequency
 * @returns Default grace period in days
 */
export const getDefaultBufferTime = (frequency: Frequency): number => {
  const frequencyDefaults: Record<Frequency, number> = {
    'Daily': 1,
    'Weekly': 1,
    'Fortnightly': 2,
    'Monthly': 3,
    'Quarterly': 7,
    'Half-Yearly': 14,
    'Yearly': 30
  };

  return frequencyDefaults[frequency] || 1;
};

/**
 * Get grace period options for dropdown
 * Grace period options are limited to not exceed the frequency duration
 * @param frequency - The checkpoint frequency
 * @returns Array of grace period options
 */
export const getBufferTimeOptions = (frequency: Frequency): Array<{ value: string; label: string }> => {
  const maxBuffer = getMaxBufferTime(frequency);

  const options = [];
  for (let i = 0; i <= maxBuffer; i++) {
    options.push({
      value: i.toString(),
      label: i === 0 ? 'No grace period' : i === 1 ? '1 day' : `${i} days`
    });
  }

  return options;
};

/**
 * Calculate due date with grace period
 * @param dueDate - The original due date
 * @param bufferTimeDays - Grace period in days
 * @returns Date when checkpoint becomes overdue
 */
export const calculateOverdueDate = (dueDate: Date, bufferTimeDays: number = 0): Date => {
  const overdueDate = new Date(dueDate);
  overdueDate.setDate(overdueDate.getDate() + bufferTimeDays);
  return overdueDate;
};

/**
 * Check if checkpoint is overdue
 * @param dueDate - The original due date
 * @param bufferTimeDays - Grace period in days
 * @param currentDate - Current date (defaults to now)
 * @returns True if checkpoint is overdue
 */
export const isCheckpointOverdue = (
  dueDate: Date,
  bufferTimeDays: number = 0,
  currentDate: Date = new Date()
): boolean => {
  const overdueDate = calculateOverdueDate(dueDate, bufferTimeDays);
  return currentDate > overdueDate;
};

/**
 * Get checkpoint status based on due date and grace period
 * @param dueDate - The original due date
 * @param bufferTimeDays - Grace period in days
 * @param isCompleted - Whether checkpoint is completed
 * @param currentDate - Current date (defaults to now)
 * @returns Checkpoint status: 'completed', 'due', 'overdue'
 */
export const getCheckpointStatus = (
  dueDate: Date,
  bufferTimeDays: number = 0,
  isCompleted: boolean = false,
  currentDate: Date = new Date()
): 'completed' | 'due' | 'overdue' => {
  if (isCompleted) {
    return 'completed';
  }

  const overdueDate = calculateOverdueDate(dueDate, bufferTimeDays);

  if (currentDate > overdueDate) {
    return 'overdue';
  }

  return 'due';
};

/**
 * Validation result type
 */
export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate checkpoint name
 * @param name - The checkpoint name to validate
 * @returns Validation result with error message if invalid
 */
export const validateCheckpointName = (name: string): ValidationResult => {
  if (!name || name.trim().length === 0) {
    return { valid: false, error: 'Checkpoint name is required' };
  }

  const trimmedName = name.trim();

  if (trimmedName.length < VALIDATION_LIMITS.NAME_MIN) {
    return {
      valid: false,
      error: `Name must be at least ${VALIDATION_LIMITS.NAME_MIN} characters`
    };
  }

  if (trimmedName.length > VALIDATION_LIMITS.NAME_MAX) {
    return {
      valid: false,
      error: `Name must be less than ${VALIDATION_LIMITS.NAME_MAX} characters`
    };
  }

  return { valid: true };
};

/**
 * Validate checkpoint description
 * @param description - The description to validate
 * @returns Validation result with error message if invalid
 */
export const validateDescription = (description?: string): ValidationResult => {
  if (!description) {
    return { valid: true }; // Description is optional
  }

  if (description.length > VALIDATION_LIMITS.DESCRIPTION_MAX) {
    return {
      valid: false,
      error: `Description must be less than ${VALIDATION_LIMITS.DESCRIPTION_MAX} characters`
    };
  }

  return { valid: true };
};

/**
 * Validate checkpoint requirement
 * @param requirement - The requirement to validate
 * @returns Validation result with error message if invalid
 */
export const validateRequirement = (requirement?: string): ValidationResult => {
  if (!requirement) {
    return { valid: true }; // Requirement is optional
  }

  if (requirement.length > VALIDATION_LIMITS.REQUIREMENT_MAX) {
    return {
      valid: false,
      error: `Requirement must be less than ${VALIDATION_LIMITS.REQUIREMENT_MAX} characters`
    };
  }

  return { valid: true };
};

/**
 * Validate buffer time against frequency constraints
 * @param bufferDays - The buffer time in days
 * @param frequency - The checkpoint frequency
 * @returns Validation result with error message if invalid
 */
export const validateBufferTime = (bufferDays: number, frequency: Frequency): ValidationResult => {
  if (bufferDays < 0) {
    return { valid: false, error: 'Buffer time cannot be negative' };
  }

  const maxBuffer = getMaxBufferTime(frequency);

  if (bufferDays > maxBuffer) {
    const frequencyDisplay = frequency.replace('-', ' ');
    return {
      valid: false,
      error: `Buffer time cannot exceed ${maxBuffer} day${maxBuffer !== 1 ? 's' : ''} for ${frequencyDisplay} frequency`
    };
  }

  return { valid: true };
};
